import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { useAuth } from "../auth/AuthProvider";
import { useMyBrand, useTenantBrand } from "../api/brandHooks";
import { DEFAULT_BRAND, type Brand } from "../types/brand";

/** Бренд на экране (T10). До ответа сервера — значения по умолчанию, те же,
 *  что были зашиты раньше, поэтому вход не «мигает». Сотрудник видит бренд
 *  объекта своего отдела, остальные — бренд тенанта. Цвет акцента ставится
 *  CSS-переменными темы, заголовок вкладки — именем бренда. */
const BrandContext = createContext<Brand>(DEFAULT_BRAND);

export function useBrand(): Brand {
  return useContext(BrandContext);
}

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : null;
}

function mix(c: [number, number, number], to: number, k: number): string {
  return c.map((v) => Math.round(v + (to - v) * k)).join(" ");
}

/** Акцент бренда → переменные темы (`--c-accent*` в index.css). */
export function applyAccent(accent: string | null) {
  const root = document.documentElement.style;
  const names = ["--c-accent", "--c-accent-hover", "--c-accent-soft", "--c-accent-line"];
  const rgb = accent ? hexToRgb(accent) : null;
  if (!rgb) {
    names.forEach((n) => root.removeProperty(n));
    return;
  }
  root.setProperty("--c-accent", rgb.join(" "));
  root.setProperty("--c-accent-hover", mix(rgb, 0, 0.15));
  root.setProperty("--c-accent-soft", mix(rgb, 255, 0.88));
  root.setProperty("--c-accent-line", mix(rgb, 255, 0.72));
}

export function BrandProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const tenant = useTenantBrand();
  const mine = useMyBrand(Boolean(user) && user?.role !== "supplier");
  const brand = useMemo<Brand>(() => mine.data ?? tenant.data ?? DEFAULT_BRAND, [mine.data, tenant.data]);

  useEffect(() => applyAccent(brand.accent), [brand.accent]);
  useEffect(() => {
    const counter = /^\(\d+\)\s*/.exec(document.title)?.[0] ?? "";
    document.title = `${counter}${brand.name} — ${brand.tagline || "закупки"}`;
  }, [brand.name, brand.tagline]);

  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>;
}

/** Значок бренда: логотип, если задан, иначе короткое имя в квадрате акцента. */
export function BrandMark({ size = "md", className = "" }: { size?: "sm" | "md" | "lg" | "xl"; className?: string }) {
  const brand = useBrand();
  const box = { sm: "h-7 w-7 rounded-md text-[0.6875rem]", md: "h-8 w-8 rounded-md text-xs", lg: "h-9 w-9 rounded-lg text-xs", xl: "h-14 w-14 rounded-2xl text-xl" }[size];
  if (brand.logo_data_url) {
    return <img src={brand.logo_data_url} alt={brand.name} className={`${box} shrink-0 object-contain ${className}`} data-testid="brand-logo" />;
  }
  return (
    <span className={`flex ${box} shrink-0 items-center justify-center bg-accent font-bold tracking-tight text-accent-fg ${className}`} data-testid="brand-mark">
      {brand.short}
    </span>
  );
}
